import { Capacitor } from '@capacitor/core';
import { Box, Button, Stack, Tooltip, Typography } from '@mui/material';
import { DrawerStickyHeader } from 'components/DrawerStickyHeader';
import { LoadingAndErrors } from 'components/LoadingAndErrors';
import { MapEntityDrawerHeader } from 'components/Map/MapEntityDrawerHeader';
import { StatItem, StatTable } from 'components/StatTable';
import { subDays } from 'date-fns';
import { useWeatherStationWithObservations } from 'features/weatherStations/hooks/useWeatherStationWithObservations';
import { useShareOrCopy } from 'hooks/useShareOrCopy';
import { Helmet } from 'react-helmet';
import { Trans, useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { getTimePass } from 'shared/dates';
import { getCurrentLocationAsShareUrl } from 'shared/utils';
import NavigationIcon from '@mui/icons-material/Navigation';
import { getDirectionFromBearing } from 'components/Map/utils';
import {
  convertCelsiusToFahrenheit,
  convertMetersPerSecToMph,
  getLatestObservation,
  parseNumericValue,
} from './WeatherStationDetails.utils';

type WindDirectionIconProps = {
  degrees: number;
};

const OBSERVATIONS_POLL_INTERVAL = 1000 * 60 * 5; // 5 minutes

const WindDirectionIcon = (
  props: WindDirectionIconProps,
): JSX.Element | null => {
  const { degrees } = props;
  return (
    <NavigationIcon sx={{ ml: 0.5, fontSize: 16, rotate: `${degrees}deg` }} />
  );
};

export const WeatherStationDetails = (): JSX.Element => {
  const { stationId } = useParams<{ stationId: string }>();
  const { t } = useTranslation();
  const observationsHistoryStart = subDays(new Date(), 7);
  const { canShareOrCopy, shareOrCopyUrl } = useShareOrCopy();

  const { weatherStation, observations, isLoading, error } =
    useWeatherStationWithObservations({
      id: parseInt(stationId, 10),
      start: observationsHistoryStart,
      end: new Date(),
      pollInterval: OBSERVATIONS_POLL_INTERVAL,
    });

  if (isLoading || error || !weatherStation) {
    return <LoadingAndErrors isLoading={isLoading} />;
  }

  const latestObservation = getLatestObservation(observations);

  const handleShare = (): void => {
    shareOrCopyUrl({
      title: t('home.riverGaugeTitle', { name: weatherStation.name }),
      url: getCurrentLocationAsShareUrl(latestObservation?.timestamp),
    });
  };

  const stats: StatItem[] = [
    {
      label: t('weatherStations.temp'),
      value:
        typeof latestObservation?.airTemp === 'number' ? (
          <Trans
            i18nKey="weatherStations.tempValue"
            values={{
              temp: convertCelsiusToFahrenheit(latestObservation.airTemp),
            }}
          >
            63
            <Typography component="span" variant="subtitle1">
              ˚F
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.dewPoint'),
      value:
        typeof latestObservation?.dewPointTemperature === 'number' ? (
          <Trans
            i18nKey="weatherStations.tempValue"
            values={{
              temp: convertCelsiusToFahrenheit(
                latestObservation?.dewPointTemperature,
              ),
            }}
          >
            33
            <Typography component="span" variant="subtitle1">
              ˚F
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.relHumidity'),
      value:
        typeof latestObservation?.relativeHumidity === 'number'
          ? t('weatherStations.percentageValue', {
              percent: parseNumericValue(latestObservation.relativeHumidity),
            })
          : '-',
    },
    {
      label: t('weatherStations.wind'),
      value:
        typeof latestObservation?.windSpeed === 'number' ? (
          <Trans
            i18nKey="weatherStations.speedValue"
            values={{
              speed: convertMetersPerSecToMph(latestObservation.windSpeed),
            }}
          >
            15
            <Typography component="span" variant="subtitle1">
              mph
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.gust'),
      value:
        typeof latestObservation?.windGust === 'number' ? (
          <Trans
            i18nKey="weatherStations.speedValue"
            values={{
              speed: convertMetersPerSecToMph(latestObservation.windGust),
            }}
          >
            25
            <Typography component="span" variant="subtitle1">
              mph
            </Typography>
          </Trans>
        ) : (
          '-'
        ),
    },
    {
      label: t('weatherStations.direction'),
      valueTypographyProps: { textTransform: 'capitalize' },
      value:
        typeof latestObservation?.windDirectionToward === 'number' ? (
          <Typography
            component="span"
            sx={{ display: 'inline-flex', alignItems: 'center' }}
          >
            <span>
              {getDirectionFromBearing(latestObservation.windDirectionToward)}
            </span>
            <WindDirectionIcon
              degrees={latestObservation.windDirectionToward}
            />
          </Typography>
        ) : (
          '-'
        ),
    },
  ];

  const shareTooltipText = Capacitor.isNativePlatform()
    ? t('common.share')
    : t('common.copyLinkToClipboard');

  return (
    <>
      <Helmet>
        <title>
          {t('home.weatherStationTitle', { name: weatherStation.name })}
        </title>
      </Helmet>

      <Box sx={{ mb: 'env(safe-area-inset-bottom)' }}>
        <DrawerStickyHeader.ContentContainer>
          <MapEntityDrawerHeader>{weatherStation.name}</MapEntityDrawerHeader>
        </DrawerStickyHeader.ContentContainer>

        <Stack sx={{ gap: 2, p: 2 }}>
          <StatTable stats={stats} />

          {latestObservation && (
            <Typography>
              {t('common.updated')}&nbsp;
              <b>{getTimePass(latestObservation.timestamp.toISOString())}</b>
            </Typography>
          )}

          {canShareOrCopy && (
            <Tooltip title={shareTooltipText}>
              <Button
                fullWidth
                color="inherit"
                variant="outlined"
                onClick={handleShare}
                size="large"
              >
                {t('common.share')}
              </Button>
            </Tooltip>
          )}
        </Stack>
      </Box>
    </>
  );
};
